import React, { useEffect, useState } from 'react';
import { DataGrid, GridToolbarColumnsButton, GridToolbarDensitySelector } from '@mui/x-data-grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import './table-container.css';
import {RiFullscreenFill} from 'react-icons/ri';
import {RiFullscreenExitLine} from 'react-icons/ri';
import PrimaryButton from '../primary-button/PrimaryButton';
import { FaLessThan } from "react-icons/fa6";
import { FaGreaterThan } from "react-icons/fa6";
import { RECORDS_PER_PAGE } from "../../const/const";

const CustomToolbar = ({ page, handlePageChange, rows, toggleFullscreen, isFullscreen, isSmallScreen, showToolbarOnSmallScreen  }) => (
  <div className="custom-toolbar">
    <div className="left-toolbar">
      {(!isSmallScreen || showToolbarOnSmallScreen) && (
        <>
          <GridToolbarColumnsButton />
          <GridToolbarDensitySelector />
          <button
            className="fullscreen-toggle"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'} >
            {isFullscreen ? <RiFullscreenExitLine /> : <RiFullscreenFill />}
          </button>
        </>
      )}
    </div>
    <CustomPagination page={page} handlePageChange={handlePageChange} rows={rows} recordsPerPage={RECORDS_PER_PAGE} />
  </div>
);

const CustomPagination = ({ page, handlePageChange, rows, recordsPerPage }) => {
  if (page === 1 && rows.length === 0) return null;
  const disableNext = rows.length < recordsPerPage;

  return (
    <div className="custom-pagination">
      <PrimaryButton
        bgColor="#004F9F"
        onClick={(e) => handlePageChange(e, page - 1)}
        disabled={page === 1}
        icon={FaLessThan}
      />
      <PrimaryButton
        bgColor="#004F9F"
        onClick={(e) => handlePageChange(e, page + 1)}
        disabled={disableNext}
        icon={FaGreaterThan}
      />
    </div>
  );
};


const calculateColumnWidths = (columns, rows) =>
  columns.map((column) => {
    let maxLength = column.headerName?.length || 0;

    rows.forEach((row) => {
      const value = row[column.field]?.toString() || '';
      maxLength = Math.max(maxLength, value.length);
    });

    const calculatedWidth = Math.min(maxLength * 10 + 20, 300);
    return {
      ...column,
      width: calculatedWidth,
    };
  });

export const renderPolicyTableRow = (label, value, isBold = false, isNoLabel = false) => (
  <div className="policy-table-row">
    {!isNoLabel && label && <span className={`policy-table-bold ${isBold ? 'policy-table-bold' : ''} policy-table-label`}>{label}</span>}
    <span>{value}</span>
  </div>
);

const TableContainer = ({ columns, rows, onRowClick = null, onPageChange = null, columnGroupingModel = [], showToolbarOnSmallScreen = true }) => {
  const isSmallScreen = useMediaQuery('(max-width: 767px)');
  const [density, setDensity] = useState('standard');
  const [adjustedColumns, setAdjustedColumns] = useState([]);
  const [page, setPage] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [numberedRows, setNumberedRows] = useState([]);

  useEffect(() => {
    setDensity('compact');
  }, []);

  useEffect(() => {
    const updatedRows = rows.map((row, index) => ({
      ...row,
      rowNumber: (page - 1) * RECORDS_PER_PAGE + index + 1,
    }));
    setNumberedRows(updatedRows); 
    const numberedColumns = [
      {
        field: 'rowNumber',
        headerName: '#',
        width: 50,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
      },
      ...columns,
    ];

    const newColumns = calculateColumnWidths(numberedColumns, updatedRows);
    setAdjustedColumns(newColumns);
  }, [columns, rows, page]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div
      className={`table-wrapper ${isFullscreen ? 'fullscreen' : ''}`}
      style={{
        position: isFullscreen ? 'fixed' : 'relative',
        top: 0,
        left: 0,
        width: isFullscreen ? '100vw' : '100%',
        height: isFullscreen ? '100vh' : '100vh',
        background: '#fff',
        zIndex: isFullscreen ? 1300 : 'auto',
      }}
    >
      {page === 1 && rows.length === 0 && (
        <div className="no-policies-popup">
          <p>No data found.</p>
        </div>
      )}

      <DataGrid
        disableColumnSorting
        rows={numberedRows}
        columns={adjustedColumns}
        columnGroupingModel={columnGroupingModel}
        columnBuffer={5}
        hideFooterPagination
        hideFooterSelectedRowCount
        hideFooter
        hideFooterRowCount
        slots={{
          toolbar: (props) => <CustomToolbar {...props} page={page} handlePageChange={handlePageChange} rows={rows} toggleFullscreen={toggleFullscreen} isFullscreen={isFullscreen} isSmallScreen={isSmallScreen} showToolbarOnSmallScreen={showToolbarOnSmallScreen} />,
        }}
        density={density}
        onDensityChange={(newDensity) => setDensity(newDensity)}
        components={{
          Footer: () => null,
        }}
        sx={{
          width: '100%',
          height: isFullscreen ? '100vh' : 'calc(84vh - 100px)',
          overflowY: 'auto',
          '& .MuiDataGrid-scrollbar': {
            zIndex: 1,
          },
          '& .MuiDataGrid-cell': {
            fontSize: '12px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: '1',
          },
          '& .status-cell': {
            whiteSpace: 'nowrap',
            overflow: 'visible',
            textOverflow: 'clip',
          },
          '& .MuiDataGrid-columnHeader': isSmallScreen && !showToolbarOnSmallScreen
            ? { display: 'none' }
            : {
                fontSize: '12px',
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                backgroundColor: '#fff',
              },
          '& .MuiDataGrid-root': {
            overflowX: 'auto',
          },
          '& .MuiDataGrid-row': {
            cursor: onRowClick ? 'pointer' : 'default',
          },
        }}
        onRowClick={(params) => {
          if (onRowClick) {
            onRowClick(params.row);
          }
        }}

        getRowHeight={() => (isSmallScreen && !showToolbarOnSmallScreen ? 'auto' : undefined)}
      />
    </div>
  );};

export default TableContainer;